// Персистентный ВЫБОР филиала пользователем.
//
// Отличие от sessionStorage-ключа selectedBranchId (живёт на вкладку и пишется при
// ЛЮБОЙ смене филиала, включая авто-роутинг доставки): сюда попадает только явный
// выбор пользователя — из шапки, футера, мобильного меню или модалки с картой.
// Именно этот выбор переживает обновление страницы и новые вкладки, а через
// {@link CHOICE_TTL_MS} протухает: локация пользователя могла измениться, и
// приложение снова спросит её стартовой шторкой (features/branch-select-prompt).

const STORAGE_KEY = 'branchChoice'

/**
 * sessionStorage-ключ ТЕКУЩЕГО филиала вкладки (пишется при любой смене, включая
 * авто-роутинг). Константа общая: пишет его стор, читает restorePersistedBranchId —
 * молчаливое переименование в одном месте разорвало бы цепочку восстановления.
 */
export const SESSION_BRANCH_KEY = 'selectedBranchId'

/** Срок жизни выбора: сутки. После — выбор сбрасывается, шторка спросит заново. */
export const CHOICE_TTL_MS = 24 * 60 * 60 * 1000

interface StoredBranchChoice {
  branchId: number
  /** Момент выбора (Date.now()). Окно абсолютное: новый выбор — новый отсчёт. */
  savedAt: number
}

/**
 * Свежий (моложе суток) выбранный пользователем филиал или null.
 * Протухшую запись удаляет сразу — чтобы «полусвежие» данные не всплывали в других местах.
 */
export function loadBranchChoice(): number | null {
  if (!import.meta.client) return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<StoredBranchChoice>
    const branchId = Number(parsed?.branchId)
    const savedAt = Number(parsed?.savedAt)

    if (!Number.isFinite(branchId) || branchId <= 0 || !Number.isFinite(savedAt)) {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }

    if (Date.now() - savedAt > CHOICE_TTL_MS) {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }

    return branchId
  } catch {
    // localStorage недоступен (приватный режим) или битый JSON — считаем, что выбора нет.
    return null
  }
}

export function saveBranchChoice(branchId: number): void {
  if (!import.meta.client) return
  try {
    const choice: StoredBranchChoice = { branchId, savedAt: Date.now() }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(choice))
  } catch {
    // Некритично: выбор просто не переживёт перезагрузку.
  }
}

export function clearBranchChoice(): void {
  if (!import.meta.client) return
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Уже нечего чистить.
  }
}

/** Есть ли свежий (моложе суток) выбор филиала пользователем. */
export function hasFreshBranchChoice(): boolean {
  return loadBranchChoice() != null
}

/**
 * Восстановление филиала после перезагрузки — единая цепочка приоритетов:
 * явный выбор пользователя (localStorage, сутки) → состояние вкладки
 * (sessionStorage, пишется при любой смене, включая авто-роутинг).
 *
 * isUsable — проверка «филиал существует и активен» (когда филиалы уже
 * загружены). Явный выбор, не прошедший её, ЧИСТИТСЯ: иначе запись о
 * выключенном вчера филиале заставляла бы шторку молчать до суток, хотя
 * фактический выбор уже сброшен на дефолтный.
 */
export function restorePersistedBranchId(isUsable?: (branchId: number) => boolean): number | null {
  if (!import.meta.client) return null

  const chosen = loadBranchChoice()
  if (chosen != null) {
    if (!isUsable || isUsable(chosen)) return chosen
    clearBranchChoice()
  }

  try {
    const fromSession = Number(sessionStorage.getItem(SESSION_BRANCH_KEY)) || null
    if (fromSession && isUsable && !isUsable(fromSession)) return null
    return fromSession
  } catch {
    return null
  }
}
